import { useState, useEffect, useRef } from 'react';
import {
  getProducts,
  getCategories,
  getHeroSlides,
  getTestimonials,
  getCollectionImages,
  getSettings,
  addProduct as addProductService,
  updateProduct as updateProductService,
  deleteProduct as deleteProductService,
  addCategory as addCategoryService,
  updateCategory as updateCategoryService,
  deleteCategory as deleteCategoryService,
  addHeroSlide as addHeroSlideService,
  updateHeroSlide as updateHeroSlideService,
  deleteHeroSlide as deleteHeroSlideService,
  addTestimonial as addTestimonialService,
  updateTestimonial as updateTestimonialService,
  deleteTestimonial as deleteTestimonialService,
  updateSettings as updateSettingsService,
  updateCollectionImage as updateCollectionImageService,
  initializeFirebaseData,
  checkInitialized
} from '../firebase/services';
import defaultData from '../data/store-data.json';

/**
 * Custom hook for managing store data with Firebase
 */
export const useFirebaseData = () => {
  const [data, setData] = useState({
    products: [],
    categories: [],
    heroSlides: [],
    testimonials: [],
    collectionImages: [],
    settings: {},
    loading: true,
    error: null,
    initialized: false
  });

  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const initializingRef = useRef(false);

  // Load all data from Firebase
  const loadData = async () => {
    try {
      setData(prev => ({ ...prev, loading: true }));

      // First, check if Firebase has been initialized
      const settings = await getSettings();

      // If no settings or not initialized, initialize Firebase (only once)
      if (!settings || !settings.initialized) {
        // Check if initialization is already in progress
        if (initializingRef.current) {
          console.log('⏳ Initialization already in progress, waiting...');
          // Wait a bit and try loading again
          await new Promise(resolve => setTimeout(resolve, 2000));
          return loadData();
        }

        // Mark as initializing
        initializingRef.current = true;
        console.log('🔄 First time setup - initializing Firebase...');

        try {
          const initialized = await initializeFirebaseData(defaultData);

          if (initialized) {
            console.log('✅ Initialization complete, loading data...');
          }
        } finally {
          initializingRef.current = false;
        }
      }

      // Load all data
      const [products, categories, heroSlides, testimonials, collectionImages, finalSettings] = await Promise.all([
        getProducts(),
        getCategories(),
        getHeroSlides(),
        getTestimonials(),
        getCollectionImages(),
        getSettings()
      ]);

      setData({
        products,
        categories,
        heroSlides,
        testimonials,
        collectionImages,
        settings: finalSettings || defaultData.settings,
        loading: false,
        error: null,
        initialized: true
      });
    } catch (error) {
      console.error('❌ Error loading Firebase data:', error);
      setData(prev => ({
        ...prev,
        loading: false,
        error: error.message
      }));
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTrigger]);

  const refresh = () => setRefreshTrigger(prev => prev + 1);

  // Products
  const addProduct = async (product) => {
    try {
      const newProduct = await addProductService(product);
      refresh();
      return newProduct;
    } catch (error) {
      console.error('Error adding product:', error);
      throw error;
    }
  };

  const updateProduct = async (id, updates) => {
    try {
      await updateProductService(id, updates);
      refresh();
    } catch (error) {
      console.error('Error updating product:', error);
      throw error;
    }
  };

  const deleteProduct = async (id) => {
    try {
      await deleteProductService(id);
      refresh();
    } catch (error) {
      console.error('Error deleting product:', error);
      throw error;
    }
  };

  // Categories
  const addCategory = async (category) => {
    try {
      const newCategory = await addCategoryService(category);
      refresh();
      return newCategory;
    } catch (error) {
      console.error('Error adding category:', error);
      throw error;
    }
  };

  const updateCategory = async (id, updates) => {
    try {
      await updateCategoryService(id, updates);
      refresh();
    } catch (error) {
      console.error('Error updating category:', error);
      throw error;
    }
  };

  const deleteCategory = async (id) => {
    try {
      await deleteCategoryService(id);
      refresh();
    } catch (error) {
      console.error('Error deleting category:', error);
      throw error;
    }
  };

  // Hero Slides
  const addHeroSlide = async (slide) => {
    try {
      const newSlide = await addHeroSlideService(slide);
      refresh();
      return newSlide;
    } catch (error) {
      console.error('Error adding hero slide:', error);
      throw error;
    }
  };

  const updateHeroSlide = async (id, updates) => {
    try {
      await updateHeroSlideService(id, updates);
      refresh();
    } catch (error) {
      console.error('Error updating hero slide:', error);
      throw error;
    }
  };

  const deleteHeroSlide = async (id) => {
    try {
      await deleteHeroSlideService(id);
      refresh();
    } catch (error) {
      console.error('Error deleting hero slide:', error);
      throw error;
    }
  };

  // Testimonials
  const addTestimonial = async (testimonial) => {
    try {
      const newTestimonial = await addTestimonialService(testimonial);
      refresh();
      return newTestimonial;
    } catch (error) {
      console.error('Error adding testimonial:', error);
      throw error;
    }
  };

  const updateTestimonial = async (id, updates) => {
    try {
      await updateTestimonialService(id, updates);
      refresh();
    } catch (error) {
      console.error('Error updating testimonial:', error);
      throw error;
    }
  };

  const deleteTestimonial = async (id) => {
    try {
      await deleteTestimonialService(id);
      refresh();
    } catch (error) {
      console.error('Error deleting testimonial:', error);
      throw error;
    }
  };

  // Settings
  const updateSettings = async (updates) => {
    try {
      const newSettings = { ...data.settings, ...updates };
      await updateSettingsService(newSettings);
      refresh();
    } catch (error) {
      console.error('Error updating settings:', error);
      throw error;
    }
  };

  // Collection Images
  const updateCollectionImage = async (id, updates) => {
    try {
      await updateCollectionImageService(id, updates);
      refresh();
    } catch (error) {
      console.error('Error updating collection image:', error);
      throw error;
    }
  };

  return {
    ...data,
    addProduct,
    updateProduct,
    deleteProduct,
    addCategory,
    updateCategory,
    deleteCategory,
    addHeroSlide,
    updateHeroSlide,
    deleteHeroSlide,
    addTestimonial,
    updateTestimonial,
    deleteTestimonial,
    updateSettings,
    updateCollectionImage,
    refresh
  };
};
